/**
 * Apply a data-analytics-id attribute to an element to have its click events
 * automatically tracked.
 *
 * An optional `data-analytics-attributes` attribute may also be applied to
 * the element to send custom attributes with the `trackEvent`. The contents
 * of this should be a JSON.stringified object.
 *
 * e.g.
 * // Will generate an event named "Clicked - Get Started" with vehicle info.
 * <button
 *   type="button"
 *   data-analytics-id="Get Started"
 *   data-analytics-attributes={JSON.stringify({ vehicleId: 1234 })}
 * >
 *   Get Started
 * </button>
 *
 * In cases where event propagation is stopped, use the `useTrackClickEvent`
 * hook to create an event handler that can be called in the click handler
 * that stopped propagation.
 *
 * e.g.
 * const trackClickEvent = useTrackClickEvent(trackEvent);
 *
 * function onClick(ev: MouseEvent) {
 *  ev.stopPropagation();
 *  trackClickEvent(ev);
 *  // Do other stuff
 * }
 */
import { useCallback, useEffect, useState } from 'react';

type TrackEvent = (
  eventName: string,
  customAttributes?: object | undefined
) => void;

function safeParse<T = any>(input: string): T | undefined {
  try {
    return JSON.parse(input);
  } catch (ex) {
    return undefined;
  }
}

const fetchBodyElement = () => document.body;

export function useTrackClickEvent(
  trackEvent: TrackEvent,
  fireDataDogAction?: (actionName: string) => void
) {
  const [previousEvent, setPreviousEvent] = useState({
    previousEventId: '',
    previousTime: {} as Date
  });

  return useCallback(
    (ev: MouseEvent) => {
      if (!(ev.target instanceof Element)) return;

      const target = ev.target.closest<HTMLElement>('[data-analytics-id]');
      const { analyticsId, analyticsAttributes } = target?.dataset || {};

      if (!analyticsId) return;

      const { previousEventId, previousTime } = previousEvent;

      // Prevent double-clicks sending multiple events
      if (
        analyticsId === previousEventId &&
        Date.now() - previousTime.getTime() < 500
      ) {
        setPreviousEvent({
          previousEventId: analyticsId,
          previousTime: new Date()
        });

        return;
      }

      setPreviousEvent({
        previousEventId: analyticsId,
        previousTime: new Date()
      });

      trackEvent(
        `Clicked - ${analyticsId}`,
        analyticsAttributes ? safeParse(analyticsAttributes) : undefined
      );

      if (fireDataDogAction) {
        fireDataDogAction(`Clicked - ${analyticsId}`);
      }
    },
    [fireDataDogAction, previousEvent, trackEvent]
  );
}

export default function useTrackClickEvents(
  trackEvent: TrackEvent,
  fetchContainerElement: () => HTMLElement | null = fetchBodyElement,
  fireDataDogAction?: (actionName: string) => void
) {
  const trackClickEvent = useTrackClickEvent(trackEvent, fireDataDogAction);

  useEffect(() => {
    function onClick(ev: MouseEvent) {
      if (!(ev.target instanceof Element)) return;

      // Ignore events from elements that aren't in our container element, unless
      // they are disconnected from the DOM entirely, in which case we can't tell
      // if they're in our container element. In that case we'll send an event
      // for them, just in case.
      if (
        ev.target.closest('body') &&
        !fetchContainerElement()?.contains(ev.target)
      ) {
        return;
      }

      trackClickEvent(ev);
    }

    document.addEventListener('click', onClick, false);

    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [fetchContainerElement, trackClickEvent]);
}
